import type { $Fetch } from 'ofetch';
import type {
  Opportunity, OpportunityStatus, CriarOpportunityCommand,
  TransicionarOpportunityCommand, ConverterOpportunityCommand,
} from '@/domain/model/Opportunity';
import type { Venda } from '@/domain/model/Venda';
import type { Page } from '@/domain/model/Page';
import type { OpportunityGateway } from '@/application/port/out/OpportunityGateway';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const PATH = '/api/v1/commerce/opportunities';

export class HttpOpportunityGateway implements OpportunityGateway {
  constructor(private readonly client: $Fetch) {}

  async list(p: { status?: OpportunityStatus; leadId?: string; vendedorUserId?: string; page?: number; size?: number }): Promise<Page<Opportunity>> {
    const params: Record<string, string | number> = {};
    if (p.status) params['status'] = p.status;
    if (p.leadId) params['leadId'] = p.leadId;
    if (p.vendedorUserId) params['vendedorUserId'] = p.vendedorUserId;
    if (typeof p.page === 'number') params['page'] = p.page;
    if (typeof p.size === 'number') params['size'] = p.size;
    const res = await this.client<ApiResponse<SpringPage<Opportunity>>>(PATH, { method: 'GET', params });
    assertApiSuccess(res);
    const d = res.data;
    return { content: d.content, number: d.number, size: d.size, totalElements: d.totalElements, totalPages: d.totalPages, first: d.first, last: d.last };
  }

  async get(id: string): Promise<Opportunity> {
    const res = await this.client<ApiResponse<Opportunity>>(`${PATH}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async criar(command: CriarOpportunityCommand): Promise<Opportunity> {
    const res = await this.client<ApiResponse<Opportunity>>(PATH, { method: 'POST', body: command });
    assertApiSuccess(res);
    return res.data;
  }

  async transicionar(id: string, command: TransicionarOpportunityCommand): Promise<Opportunity> {
    const res = await this.client<ApiResponse<Opportunity>>(
      `${PATH}/${encodeURIComponent(id)}/transicionar`, { method: 'POST', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async converter(id: string, command: ConverterOpportunityCommand): Promise<Venda> {
    const res = await this.client<ApiResponse<Venda>>(
      `${PATH}/${encodeURIComponent(id)}/converter`, { method: 'POST', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }
}
